import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { X, History, ArrowDownCircle, ArrowUpCircle, Wallet, RefreshCw, Receipt, Package, Truck, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { getUserWallet, userWalletHistory, userPurchases } from '../../lib/api';
import { resolveMediaUrl } from '../admin/MediaUpload';
import { formatCompact } from '../../lib/format';

function fmt(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }

const DELIVERY_BADGES = {
  pending: { label: 'Pending', Icon: Clock, color: 'bg-amber-500/20 text-amber-300' },
  processing: { label: 'Processing', Icon: Truck, color: 'bg-sky-500/20 text-sky-300' },
  delivered: { label: 'Delivered', Icon: CheckCircle2, color: 'bg-emerald-500/20 text-emerald-300' },
  cancelled: { label: 'Cancelled', Icon: XCircle, color: 'bg-red-500/20 text-red-300' },
};

export default function HistoryModal({ open, onOpenChange }) {
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [tab, setTab] = useState('txn');
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [w, h, pur] = await Promise.all([getUserWallet(), userWalletHistory(), userPurchases().catch(() => [])]);
      setWallet(w); setHistory(h); setPurchases(pur || []);
    } catch (e) { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) reload(); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-[560px] max-h-[92vh] sm:max-h-[88vh] bg-[#0f0f0f] border border-zinc-800 text-zinc-100 p-0 rounded-2xl [&>button]:hidden overflow-hidden flex flex-col"
        data-testid="history-modal"
      >
        <DialogTitle className="sr-only">My Wallet</DialogTitle>
        <DialogDescription className="sr-only">Wallet balance, transactions and deposit history.</DialogDescription>
        <span className="absolute right-3 top-3 z-20">
          <button onClick={() => onOpenChange(false)} className="w-9 h-9 rounded-full text-zinc-300 hover:text-white bg-black/40 hover:bg-red-500/80 border border-white/10 flex items-center justify-center" data-testid="history-close-btn"><X size={18} /></button>
        </span>

        {/* Header (sticky) — compact balance */}
        <div className="shrink-0 px-4 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-[color:var(--gold)]/20 via-zinc-950 to-zinc-950">
          <div className="flex items-center gap-2 text-[color:var(--gold)] mb-1"><Wallet size={18} /> <span className="font-bold uppercase tracking-wider text-xs">My Wallet</span></div>
          <div className="text-zinc-400 text-[11px]">Available Balance</div>
          <div className="text-3xl sm:text-4xl font-extrabold tabular-nums notranslate" translate="no" data-testid="history-balance">
            ₹{wallet ? formatCompact(wallet.balance) : '—'}
          </div>
          <button onClick={reload} className="mt-1.5 text-[11px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1 casino-spin-hover" data-testid="history-refresh-btn"><RefreshCw size={11} /> Refresh</button>
        </div>

        {/* Tabs (sticky) */}
        <div className="shrink-0 flex border-b border-zinc-800 px-2 bg-[#0f0f0f]">
          {[{ k: 'txn', l: 'Transactions', i: History }, { k: 'pur', l: 'My Purchases', i: Package }, { k: 'dep', l: 'Deposits', i: Receipt }].map((t) => {
            const Icon = t.i;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} className={`flex-1 px-3 py-2.5 text-sm font-semibold border-b-2 flex items-center justify-center gap-1.5 ${tab === t.k ? 'text-[color:var(--gold)] border-[color:var(--gold)]' : 'text-zinc-400 border-transparent hover:text-zinc-200'}`} data-testid={`history-tab-${t.k}`}>
                <Icon size={14} /> {t.l}
              </button>
            );
          })}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto chat-scroll p-3 sm:p-4 space-y-2 min-h-0">
          {loading && <div className="text-zinc-500 text-sm p-4 text-center">Loading…</div>}
          {!loading && tab === 'txn' && (
            (history?.transactions || []).length === 0
              ? <div className="text-zinc-500 text-sm p-6 text-center">No transactions yet.</div>
              : history.transactions.map((t) => (
                <div key={t.id} className="rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2.5 flex items-center justify-between" data-testid={`history-txn-${t.id}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    {t.type === 'deposit' ? <ArrowDownCircle size={20} className="text-emerald-400 shrink-0" /> : <ArrowUpCircle size={20} className="text-red-400 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-bold text-zinc-100 truncate text-sm">{t.note || (t.type === 'deposit' ? 'Wallet credit' : 'Wallet debit')}</div>
                      <div className="text-zinc-500 text-[10px]">{fmt(t.created_at)}</div>
                    </div>
                  </div>
                  <div className={`shrink-0 ml-2 font-extrabold tabular-nums text-sm sm:text-base notranslate ${t.type === 'deposit' ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                    {t.type === 'deposit' ? '+' : '−'}₹{formatCompact(t.amount)}
                  </div>
                </div>
              ))
          )}
          {!loading && tab === 'pur' && (
            purchases.length === 0
              ? <div className="text-zinc-500 text-sm p-6 text-center">No purchases yet.</div>
              : purchases.map((p) => {
                const b = DELIVERY_BADGES[p.delivery_status] || DELIVERY_BADGES.pending;
                const BIcon = b.Icon;
                return (
                  <div key={p.id} className="rounded-lg bg-zinc-950 border border-zinc-800 p-3" data-testid={`history-pur-${p.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold text-zinc-100 truncate text-sm">{p.service_name || p.item_id}</div>
                        <div className="text-zinc-500 text-[10px]">{fmt(p.created_at)} · <span className="notranslate" translate="no">₹{formatCompact(p.amount)}</span></div>
                        {p.delivery_note && <div className="text-zinc-300 text-[11px] mt-1 italic">"{p.delivery_note}"</div>}
                      </div>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded-full shrink-0 ${b.color}`} data-testid={`history-pur-status-${p.id}`}>
                        <BIcon size={11} /> {b.label}
                      </span>
                    </div>
                  </div>
                );
              })
          )}
          {!loading && tab === 'dep' && (
            (history?.deposits || []).length === 0
              ? <div className="text-zinc-500 text-sm p-6 text-center">No deposit requests yet.</div>
              : history.deposits.map((d) => (
                <div key={d.id} className="rounded-lg bg-zinc-950 border border-zinc-800 p-3" data-testid={`history-dep-${d.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {d.screenshot_url && <img src={resolveMediaUrl(d.screenshot_url)} alt="" className="w-12 h-12 rounded-md object-cover border border-zinc-800 shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-extrabold text-zinc-100 text-base sm:text-lg tabular-nums notranslate" translate="no">₹{formatCompact(d.amount)}</div>
                        <div className="text-zinc-500 text-[10px]">{fmt(d.created_at)}</div>
                        {d.reject_note && <div className="text-red-400 text-[11px] truncate">Rejected: {d.reject_note}</div>}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full shrink-0 ${d.status === 'pending' ? 'bg-amber-500/20 text-amber-300' : d.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{d.status}</span>
                  </div>
                </div>
              ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
